import { useMemo, useState } from 'react';
import { BaseRecord } from '@refinedev/core';
import { DataTableColumn, DataTableSortStatus } from 'mantine-datatable';

export default function useSortableData<T extends BaseRecord>(
  data: T[],
  columns: DataTableColumn<T>[],
  initialSort?: { columnAccessor: string; direction: 'asc' | 'desc' },
) {
  const defaultColumn = initialSort
    ? columns.find(c => c.accessor === initialSort.columnAccessor)
    : columns.find(c => c.sortable);

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T>>({
    columnAccessor: defaultColumn?.accessor ?? '',
    sortKey: defaultColumn?.sortKey ?? '',
    direction: initialSort?.direction ?? 'asc',
  });

  const sortedData = useMemo(() => {
    if (defaultColumn == null || !sortStatus.sortKey) {
      return data;
    }
    return [...data].sort((a, b) => {
      const sortKey = sortStatus.sortKey as string;

      const getValue = (obj: T): unknown => {
        if (!sortKey.includes('.')) {
          return obj[sortKey as keyof T];
        }

        const parts = sortKey.split('.');
        let value: unknown = obj;
        for (const part of parts) {
          value = value ? (value as Record<string, unknown>)[part] : undefined;
        }
        return value;
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortStatus.direction === 'desc' ? bValue - aValue : aValue - bValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortStatus.direction === 'desc'
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      return 0;
    });
  }, [sortStatus, data]);

  return { sortStatus, setSortStatus, sortedData };
}
